import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Loader from "../../../Component/Loader/Loader";
import Pill, { PillTone } from "../../../Component/Pill";
import RowMenu, { RowMenuItem } from "../../../Component/RowMenu";
import { getRequest, postRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import AssignPlanModal from "../modals/AssignPlanModal";
import AssignAffiliateModal from "../modals/AssignAffiliateModal";
import WabaDetailsModal from "../modals/WabaDetailsModal";
import ManageCreditsModal from "../modals/ManageCreditsModal";
import EditProfileModal from "../modals/EditProfileModal";
import EditBusinessModal from "../modals/EditBusinessModal";
import ConfirmActionModal, { ConfirmAction } from "../modals/ConfirmActionModal";
import CustomerWalletTab from "../components/CustomerWalletTab";
import CustomerInvoicesTab from "../components/CustomerInvoicesTab";
// static design actions (not connected to the backend yet)
import AdjustWalletModal from "../modals/AdjustWalletModal";
import ExtendValidityModal from "../modals/ExtendValidityModal";
import "./customer-details.css";

// Tabs of the customer details page (same as the new design)
const CD_TABS: [string, string, string][] = [
  ["overview", "ri-dashboard-line", "Overview"],
  ["billing", "ri-price-tag-3-line", "Subscription & billing"],
  ["wallet", "ri-wallet-3-line", "Wallet"],
  ["invoices", "ri-file-list-3-line", "Invoices"],
  ["channels", "ri-whatsapp-line", "Channels & numbers"],
  ["team", "ri-team-line", "Team"],
  ["automation", "ri-flow-chart", "Automation"],
  ["tickets", "ri-customer-service-2-line", "Tickets"],
  ["activity", "ri-history-line", "Activity"],
  ["settings", "ri-settings-3-line", "Settings"],
];

const STATUS_PILL: Record<number, [PillTone, string]> = {
  1: ["ok", "Active"],
  0: ["warn", "Inactive"],
  2: ["bad", "Trash"],
};

const QUALITY: Record<string, [PillTone, string]> = {
  GREEN: ["ok", "Green"],
  YELLOW: ["warn", "Yellow"],
  RED: ["bad", "Red"],
};

const inr = (n: number) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const human = (s?: string) => (s ? s.replace(/_/g, " ").toLowerCase() : "");
const fmtDate = (d?: string) => (d ? moment(d).format("DD MMM YYYY") : "—");

// Profile / business fields edited through EditProfileModal & EditBusinessModal
const mapResponseData = (data: any) => ({
  user_first_name: data?.user_first_name || "",
  user_last_name: data?.user_last_name || "",
  user_email: data?.user_email || "",
  user_mobile: data?.user_mobile || "",
  user_companyName: data?.user_companyName || "",
  user_website: data?.user_website || "",
  user_country: data?.user_country || "",
  user_state: data?.user_state || "",
  user_industry: data?.user_industry || "",
  user_companySizeRange: data?.user_companySizeRange || "",
});

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>(null);
  const [isLoader, setIsLoader] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [showWabaModal, setShowWabaModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  // static design modals: "wallet" | "extend"
  const [staticModal, setStaticModal] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // bumped after a plan change / wallet adjustment so the Wallet & Invoices tabs reload
  const [billingReload, setBillingReload] = useState(0);

  // Edit forms
  const [form, setForm] = useState(mapResponseData(null));
  const [isSaving, setIsSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "" });

  // Fetch Customer Details
  const getUserDetails = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await getRequest(`/user/userDetails/${id}`);

      if (response.status === 'success') {
        setUserData(response.data);
        setForm(mapResponseData(response.data));
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading customer details!");
    } finally {
      setIsLoader(false);
    }
  }, [id]);

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === "user_country" ? { user_state: "" } : {}),
    }));
  };

  // Update Profile / Business details
  const handleProfileUpdate = async () => {
    try {
      setIsSaving(true);
      const response = await postRequest(`/user/updateUserDetails/${id}`, form);
      if (response.status === 'success') {
        toast.success(response.message || "Profile updated successfully");
        setShowProfileModal(false);
        setShowBusinessModal(false);
        getUserDetails();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error updating profile");
    } finally {
      setIsSaving(false);
    }
  };

  // Update Password
  const handlePasswordUpdate = async () => {
    if (passwordForm.newPassword.length < 6) return toast.warning("Password must be at least 6 characters");
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return toast.warning("Passwords do not match");
    try {
      setIsLoader(true);
      const response = await postRequest(`/user/updateUserPassword/${id}`, { newPassword: passwordForm.newPassword });
      if (response.status === 'success') {
        toast.success("Password updated successfully");
        setPasswordForm({ newPassword: "", confirmPassword: "" });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error updating password");
    } finally {
      setIsLoader(false);
    }
  };

  // Verify Email Manually
  const handleVerifyEmail = async () => {
    try {
      setIsLoader(true);
      const response = await postRequest(`/user/verifyUserEmail/${id}`, {});
      if (response.status === 'success') {
        toast.success("Email verified successfully");
        getUserDetails();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error verifying email");
    } finally {
      setIsLoader(false);
    }
  };

  // Login as this customer (same as the customer list)
  const handleDirectLogin = async () => {
    try {
      const response = await postRequest(`/user/admin-login-as-user`, { user_id: id });
      if (response.status === 'success') {
        toast.success("Opening user dashboard...");
        localStorage.setItem("temp_impersonate_data", JSON.stringify(response.data));
        const app = userData?.user_is_credit_line_enabled ? "https://wa.uomox.com" : "https://app.uomox.com";
        window.open(`${app}/auto-login?token=${response.token}`, '_blank');
      } else {
        toast.error(response.message || "Failed to login as this user");
      }
    } catch (error) {
      toast.error("Something went wrong during direct login.");
    }
  };

  const handleReconcileWallet = async () => {
    if (!window.confirm("Are you sure you want to reconcile this user's wallet ledger?")) return;
    try {
      setIsLoader(true);
      const response = await postRequest(`/user/reconcile-wallet`, { userId: id });
      if (response.status === 'success') {
        toast.success(response.message || "Wallet reconciled successfully!");
        getUserDetails();
      } else {
        toast.error(response.message || "Failed to reconcile wallet.");
      }
    } catch (error) {
      toast.error("Something went wrong while reconciling the wallet.");
    } finally {
      setIsLoader(false);
    }
  };

  // Status / trash actions → /user/handleBulkAction (1 Active, 2 Inactive, 3 Trash, 5 Restore, 6 Delete)
  const applyStatusAction = async (code: string) => {
    try {
      setIsLoader(true);
      const response = await postRequest(`/user/handleBulkAction`, { bulkAction: code, selectedUsers: [id] });
      if (response.status === 'success') {
        toast.success("Customer updated");
        if (code === "6") navigate("/customers-and-users");
        else getUserDetails();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error updating customer");
    } finally {
      setIsLoader(false);
    }
  };

  const confirmStatusAction = (code: string) => {
    const run = () => { setConfirmAction(null); applyStatusAction(code); };
    const actions: Record<string, ConfirmAction> = {
      "1": { icon: "ri-play-circle-line", tone: "ok", title: `Activate ${name}?`, body: "The workspace goes live again.", confirmLabel: "Activate", onConfirm: run },
      "2": { icon: "ri-pause-circle-line", tone: "warn", title: `Deactivate ${name}?`, body: "Broadcasts, bots and API calls stop right away.", confirmLabel: "Deactivate", danger: true, onConfirm: run },
      "3": { icon: "ri-delete-bin-line", tone: "bad", title: `Move ${name} to trash?`, body: "It can be restored later from the Trash tab.", confirmLabel: "Move to trash", danger: true, onConfirm: run },
      "5": { icon: "ri-arrow-go-back-line", tone: "ok", title: `Restore ${name}?`, body: "The customer moves back out of the trash.", confirmLabel: "Restore", onConfirm: run },
      "6": { icon: "ri-delete-bin-line", tone: "bad", title: `Permanently delete ${name}?`, body: "This cannot be undone.", confirmLabel: "Delete permanently", danger: true, onConfirm: run },
    };
    setConfirmAction(actions[code]);
  };

  // Adjust wallet: real for credit-line customers, static design otherwise (same as the list)
  const openWallet = () => (userData?.user_is_credit_line_enabled ? setShowCreditModal(true) : setStaticModal("wallet"));

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  // Plan change / wallet adjustment create invoices → refresh the customer and the billing tabs
  const onBillingChange = () => {
    getUserDetails();
    setBillingReload(n => n + 1);
  };

  if (!userData) {
    return (
      <div className="container-fluid">
        {isLoader && <Loader />}
        <div className="cx">
          <ul className="cx-cd-bc"><li><button type="button" onClick={() => navigate("/customers-and-users")}>Customers</button></li><li>Details</li></ul>
          {!isLoader && <div className="cx-card"><div className="cx-empty"><i className="ri-user-search-line"></i>Customer not found.</div></div>}
        </div>
      </div>
    );
  }

  // --- Derived values ---
  const u = userData;
  const owner = `${u.user_first_name || ""} ${u.user_last_name || ""}`.trim();
  const name = u.user_companyName || owner || u.user_email;
  const initials = name.split(/\s+/).map((w: string) => w[0]).slice(0, 2).join("").toUpperCase();
  const [sc, sl]: [PillTone, string] = STATUS_PILL[u.user_status] || ["none", "Unknown"];
  const wa = u.user_meta_analytics || {};
  const waba = u.user_meta_account_info || {};
  const wabaConnected = !!u.user_whatsapp?.isWabaConnected;
  const [qTone, qLabel]: [PillTone, string] = QUALITY[wa.quality_rating] || ["none", wabaConnected ? "Unknown" : "No number"];
  const credit = !!u.user_is_credit_line_enabled;
  const planEnd = u.user_plan_end_date ? moment(u.user_plan_end_date) : null;
  const planExpired = !!planEnd && planEnd.isBefore(moment(), "day");
  const planActive = !!u.user_plan_name && !!planEnd && !planExpired;
  const daysLeft = planEnd ? planEnd.diff(moment().startOf("day"), "days") : null;
  const msgLimit = wa.messaging_limit_tier || wa.whatsapp_business_manager_messaging_limit;

  // Onboarding progress from the real account fields
  const obSteps: [string, boolean, string][] = [
    ["Registered", true, fmtDate(u.createdAt)],
    ["Email verified", !!u.user_mail_verification_status, u.user_mail_verification_status ? "Verified" : "Pending"],
    ["WABA connected", wabaConnected, wabaConnected ? (wa.display_phone_number || "Connected") : "Pending"],
    ["Plan active", planActive, planActive ? `${u.user_plan_name} · till ${fmtDate(u.user_plan_end_date)}` : planExpired ? "Plan expired" : "Pending"],
    ["Account setup", Number(u.user_account_setup) > 0, Number(u.user_account_setup) > 0 ? "Completed" : "Pending"],
  ];
  const obDone = obSteps.filter(s => s[1]).length;
  const obNow = obSteps.findIndex(s => !s[1]);

  const menuItems: RowMenuItem[] = [
    { icon: "ri-user-settings-line", label: "Edit profile", onClick: () => setShowProfileModal(true) },
    { icon: "ri-building-2-line", label: "Edit business", onClick: () => setShowBusinessModal(true) },
    { icon: "ri-shield-user-line", label: "Assign affiliate", onClick: () => setShowAffiliateModal(true) },
    ...(wabaConnected ? [{ icon: "ri-whatsapp-line", label: "WABA details", onClick: () => setShowWabaModal(true) }] : []),
    ...(credit ? [
      { icon: "ri-refresh-line", label: "Reconcile wallet", onClick: handleReconcileWallet },
      { icon: "ri-price-tag-3-line", label: "Manage Meta pricing", onClick: () => navigate(`/meta-pricing/${id}`) },
    ] : []),
    "divider",
    ...(u.user_status === 2
      ? [
        { icon: "ri-arrow-go-back-line", label: "Restore", onClick: () => confirmStatusAction("5") },
        { icon: "ri-delete-bin-line", label: "Delete permanently", onClick: () => confirmStatusAction("6"), danger: true },
      ]
      : [
        u.user_status === 1
          ? { icon: "ri-pause-circle-line", label: "Deactivate", onClick: () => confirmStatusAction("2"), danger: true }
          : { icon: "ri-play-circle-line", label: "Activate", onClick: () => confirmStatusAction("1") },
        { icon: "ri-delete-bin-line", label: "Move to trash", onClick: () => confirmStatusAction("3"), danger: true },
      ]),
  ];

  // Tabs whose data has no admin API yet
  const notConnected = (icon: string, text: string) => (
    <div className="cx-empty"><i className={icon}></i>{text}<div className="fs12" style={{ marginTop: 4 }}>Not connected to the backend yet.</div></div>
  );

  // --- Tab bodies ---
  const renderOverview = () => (
    <div className="cx-grid g21">
      <div className="cx-vstack">
        <div className="cx-card">
          <div className="cx-card-h"><h6>Onboarding progress</h6><span className="sub">{obDone === 5 ? "Completed" : `Step ${obDone} of 5`}</span></div>
          <div className="cx-card-b">
            <div className="cx-ob-steps">
              {obSteps.map(([label, done, note], i) => (
                <div key={label} className={`cx-ob-step ${done ? "done" : i === obNow ? "now" : ""}`}>
                  <span className="dot">{done ? <i className="ri-check-line"></i> : i + 1}</span>
                  <div><b>{label}</b><small>{note}</small></div>
                </div>
              ))}
            </div>
            {!u.user_mail_verification_status && (
              <div style={{ marginTop: 12 }}>
                <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={handleVerifyEmail}><i className="ri-mail-check-line"></i>Mark email verified</button>
              </div>
            )}
          </div>
        </div>

        <div className="cx-card">
          <div className="cx-card-h"><h6>WhatsApp number</h6><Pill tone={wabaConnected ? "ok" : "none"}>{wabaConnected ? "Connected" : "Not connected"}</Pill></div>
          <div className="cx-card-b">
            {wabaConnected ? (
              <div className="cx-kv k3">
                <div><small>Number</small><b className="mono">{wa.display_phone_number || "—"}</b></div>
                <div><small>Display name</small><b>{wa.verified_name || waba.name || "—"}</b></div>
                <div><small>Quality</small><b>{qLabel}</b></div>
                <div><small>Messaging limit</small><b>{msgLimit ? human(msgLimit) : "—"}</b></div>
                <div><small>Number status</small><b>{wa.status ? human(wa.status) : "—"}</b></div>
                <div><small>Last synced</small><b>{u.user_dash_lastFetchedAt ? moment(u.user_dash_lastFetchedAt).fromNow() : "Never"}</b></div>
              </div>
            ) : notConnected("ri-whatsapp-line", "WABA not connected yet")}
          </div>
        </div>
      </div>

      <div className="cx-vstack">
        <div className="cx-card">
          <div className="cx-card-h"><h6>Business profile</h6><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setShowBusinessModal(true)}><i className="ri-edit-line"></i>Edit</button></div>
          <div className="cx-card-b">
            {[
              ["Company", u.user_companyName || "—"],
              ["Owner", owner || "—"],
              ["Email", u.user_email || "—"],
              ["Phone", u.user_mobile ? `+${u.user_country_code || "91"} ${u.user_mobile}` : "—"],
              ["Website", u.user_website || "—"],
              ["Country", u.country_details?.country_name || "—"],
              ["State", u.state_details?.state_name || "—"],
              ["Industry", u.user_industry || "—"],
              ["Company size", u.user_companySizeRange || "—"],
            ].map(([l, v]) => <div className="cx-pr" key={l}><span>{l}</span><b>{v}</b></div>)}
          </div>
        </div>

        <div className="cx-card">
          <div className="cx-card-h"><h6>Account health</h6></div>
          <div className="cx-card-b">
            <div className="cx-orow">
              <div className={`cx-ic ${qTone === "none" ? "blue" : qTone}`}><i className="ri-whatsapp-line"></i></div>
              <div className="grow"><div className="t">Number quality</div><div className="d mono">{wa.display_phone_number || "—"}</div></div>
              <div className="right"><Pill tone={qTone}>{qLabel}</Pill></div>
            </div>
            <div className="cx-orow">
              <div className={`cx-ic ${planExpired ? "bad" : planActive ? "ok" : "blue"}`}><i className="ri-price-tag-3-line"></i></div>
              <div className="grow"><div className="t">Subscription</div><div className="d">{planExpired ? `Expired ${fmtDate(u.user_plan_end_date)}` : planActive ? `${daysLeft} days left` : "No active plan"}</div></div>
              <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setShowPlanModal(true)}>Change</button></div>
            </div>
            <div className="cx-orow">
              <div className={`cx-ic ${Number(u.user_wallet_balance || 0) < 500 ? "bad" : "ok"}`}><i className="ri-wallet-3-line"></i></div>
              <div className="grow"><div className="t">Wallet</div><div className="d">{inr(u.user_wallet_balance)} · {Number(u.user_wallet_balance || 0) < 500 ? "low balance" : "healthy balance"}</div></div>
              <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={openWallet}>Add</button></div>
            </div>
            <div className="cx-orow">
              <div className={`cx-ic ${credit ? "ok" : "blue"}`}><i className="ri-bank-card-2-line"></i></div>
              <div className="grow"><div className="t">Credit line</div><div className="d">{credit ? "Enabled · postpaid" : "Not enabled"}</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBilling = () => (
    <div className="cx-grid g21">
      <div className="cx-card">
        <div className="cx-card-h">
          <h6>Current subscription</h6>
          <Pill tone={planExpired ? "bad" : planActive ? "ok" : "none"}>{planExpired ? "Expired" : planActive ? "Active" : "No plan"}</Pill>
        </div>
        <div className="cx-card-b">
          <div className="cx-sub-hero">
            <div>
              <div className="muted fs12">Plan</div>
              <div className="cx-sub-plan">{u.user_plan_name || "—"}</div>
              <div className="fs12 muted">{u.user_plan_type ? human(u.user_plan_type) : ""}{planEnd ? ` · ${planExpired ? "ended" : "renews"} ${fmtDate(u.user_plan_end_date)}` : ""}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="muted fs12">{planExpired ? "Expired" : "Days left"}</div>
              <div className="cx-sub-amt">{daysLeft === null ? "—" : planExpired ? `${Math.abs(daysLeft)}d ago` : daysLeft}</div>
            </div>
          </div>
          <div className="cx-kv mt">
            <div><small>Started</small><b>{fmtDate(u.user_plan_start_date)}</b></div>
            <div><small>Ends</small><b>{fmtDate(u.user_plan_end_date)}</b></div>
            <div><small>Billing cycle</small><b>{u.user_plan_type ? human(u.user_plan_type) : "—"}</b></div>
            <div><small>Free period</small><b>{u.user_plan_free_period ? `${u.user_plan_free_period} days` : "—"}</b></div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <button type="button" className="cx-btn cx-btn-primary cx-btn-sm" onClick={() => setShowPlanModal(true)}><i className="ri-exchange-line"></i>Change plan</button>
            <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => setStaticModal("extend")}><i className="ri-time-line"></i>Extend validity</button>
          </div>
        </div>
      </div>
      <div className="cx-card">
        <div className="cx-card-h"><h6>Billing contact</h6><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setShowBusinessModal(true)}><i className="ri-edit-line"></i>Edit</button></div>
        <div className="cx-card-b">
          {[
            ["Billing name", u.user_companyName || owner || "—"],
            ["Email", u.user_email || "—"],
            ["Phone", u.user_mobile || "—"],
            ["State", u.state_details?.state_name || "—"],
            ["Country", u.country_details?.country_name || "—"],
          ].map(([l, v]) => <div className="cx-pr" key={l}><span>{l}</span><b>{v}</b></div>)}
        </div>
      </div>
    </div>
  );

  // Wallet tab: this customer's wallet_topup invoices
  const renderWallet = () => (
    <CustomerWalletTab
      userId={id || ""}
      walletBalance={Number(u.user_wallet_balance || 0)}
      creditLine={credit}
      reloadKey={billingReload}
      actions={<>
        {credit && <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={handleReconcileWallet}><i className="ri-refresh-line"></i>Reconcile</button>}
        {credit && <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => navigate(`/meta-pricing/${id}`)}><i className="ri-price-tag-3-line"></i>Meta pricing</button>}
        <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => navigate(`/wallet?client=${id}`)}><i className="ri-external-link-line"></i>Open in wallet</button>
        <button type="button" className="cx-btn cx-btn-primary cx-btn-sm" onClick={openWallet}><i className="ri-add-line"></i>Adjust wallet</button>
      </>}
    />
  );

  // Invoices tab: every invoice of this customer (plans, renewals, wallet top-ups)
  const renderInvoices = () => <CustomerInvoicesTab userId={id || ""} reloadKey={billingReload} />;

  const renderChannels = () => {
    const ig = u.user_instagram || {};
    return (
      <div className="cx-grid g2">
        <div className="cx-card">
          <div className="cx-card-h"><h6>WhatsApp Business Account</h6><Pill tone={wabaConnected ? "ok" : "none"}>{wabaConnected ? "Connected" : "Not connected"}</Pill></div>
          <div className="cx-card-b">
            {wabaConnected ? (
              <>
                {[
                  ["Phone number", wa.display_phone_number || "—"],
                  ["Display name", wa.verified_name || "—"],
                  ["Quality rating", qLabel],
                  ["Messaging limit", msgLimit ? human(msgLimit) : "—"],
                  ["Number status", wa.status ? human(wa.status) : "—"],
                  ["Code verification", wa.code_verification_status ? human(wa.code_verification_status) : "—"],
                  ["Mode", u.user_whatsapp_type === "coexistence" ? "Coexistence" : "Cloud API"],
                  ["WABA name", waba.name || "—"],
                  ["WABA id", u.user_whatsapp?.wabaId || "—"],
                  ["Phone number id", u.user_whatsapp?.phoneNumberId || "—"],
                ].map(([l, v]) => <div className="cx-pr" key={l}><span>{l}</span><b className="mono">{v}</b></div>)}
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => setShowWabaModal(true)}><i className="ri-refresh-line"></i>Details & sync from Meta</button>
                </div>
              </>
            ) : (
              <div className="cx-empty"><i className="ri-whatsapp-line"></i>WABA not connected yet
                {u.user_whatsapp?.error_message && <div className="fs12 down" style={{ marginTop: 6 }}>{u.user_whatsapp.error_message}</div>}
              </div>
            )}
          </div>
        </div>
        <div className="cx-card">
          <div className="cx-card-h"><h6>Other channels</h6></div>
          <div className="cx-card-b">
            <div className="cx-orow">
              <div className={`cx-ic ${ig.ig_account_id ? "ok" : "blue"}`}><i className="ri-instagram-line"></i></div>
              <div className="grow"><div className="t">Instagram</div><div className="d">{ig.ig_account_id ? `${ig.username || ig.name} · ${Number(ig.followers_count || 0).toLocaleString("en-IN")} followers` : "Not connected"}</div></div>
              <div className="right"><Pill tone={ig.ig_account_id ? "ok" : "none"}>{ig.ig_account_id ? "active" : "off"}</Pill></div>
            </div>
            <div className="cx-orow">
              <div className={`cx-ic ${u.meta_ads_auth ? "ok" : "blue"}`}><i className="ri-meta-line"></i></div>
              <div className="grow"><div className="t">Meta Ads</div><div className="d">{u.meta_ads_auth ? "Connected" : "Not connected"}</div></div>
              <div className="right"><Pill tone={u.meta_ads_auth ? "ok" : "none"}>{u.meta_ads_auth ? "active" : "off"}</Pill></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAutomation = () => {
    const integrations: [string, string, boolean][] = [
      ["Shopify", "ri-shopping-bag-3-line", Number(u.user_shopify_connection_status) === 1],
      ["Razorpay", "ri-bank-card-line", Number(u.user_razorpay_connection_status) === 1],
      ["Google Sheets", "ri-file-excel-2-line", Number(u.user_googlesheets_connection_status) === 1],
      ["HighLevel", "ri-links-line", !!u.user_highlevel_locationid],
      ["Webhook", "ri-webhook-line", !!u.user_api_webhook_url],
    ];
    return (
      <div className="cx-grid g2">
        <div className="cx-card">
          <div className="cx-card-h"><h6>Active flows</h6><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={handleDirectLogin}>Open in client app</button></div>
          {notConnected("ri-flow-chart", "Chatbot flows aren't available in the admin API yet")}
        </div>
        <div className="cx-card">
          <div className="cx-card-h"><h6>Integrations</h6></div>
          <div className="cx-card-b">
            {integrations.map(([label, icon, on]) => (
              <div className="cx-orow" key={label}>
                <div className={`cx-ic ${on ? "ok" : "blue"}`}><i className={icon}></i></div>
                <div className="grow"><div className="t">{label}</div></div>
                <div className="right"><Pill tone={on ? "ok" : "none"}>{on ? "connected" : "not set"}</Pill></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderActivity = () => {
    const events: [string, string, string | undefined][] = [
      ["", "Profile last updated", u.updatedAt],
      ["", "WhatsApp data synced from Meta", u.user_dash_lastFetchedAt],
      [planExpired ? "bad" : "ok", planExpired ? "Plan expired" : "Plan renews", u.user_plan_end_date],
      ["ok", `Plan started${u.user_plan_name ? ` · ${u.user_plan_name}` : ""}`, u.user_plan_start_date],
      ["ok", "Workspace created", u.createdAt],
    ];
    return (
      <div className="cx-grid g2">
        <div className="cx-card">
          <div className="cx-card-h"><h6>Workspace events</h6><span className="sub">from account dates</span></div>
          <div className="cx-card-b">
            <div className="cx-timeline">
              {events.filter(e => e[2]).map(([tone, text, at]) => (
                <div className={`cx-tl ${tone}`} key={text}><div className="t">{text}</div><div className="m">{moment(at).format("DD MMM YYYY · HH:mm")}</div></div>
              ))}
            </div>
          </div>
        </div>
        <div className="cx-card">
          <div className="cx-card-h"><h6>Admin actions</h6><span className="sub">audit log</span></div>
          {notConnected("ri-history-line", "The audit log isn't available in the admin API yet")}
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="cx-grid g2">
      <div className="cx-vstack">
        <div className="cx-card">
          <div className="cx-card-h"><h6>Account settings</h6></div>
          <div className="cx-card-b">
            <div className="cx-orow">
              <div className="grow"><div className="t">Profile details</div><div className="d">Owner name, email and phone</div></div>
              <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setShowProfileModal(true)}>Edit</button></div>
            </div>
            <div className="cx-orow">
              <div className="grow"><div className="t">Email verification</div><div className="d">{u.user_mail_verification_status ? "Email is verified" : "Owner hasn't verified the email yet"}</div></div>
              <div className="right">{u.user_mail_verification_status
                ? <Pill tone="ok">verified</Pill>
                : <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={handleVerifyEmail}>Mark verified</button>}
              </div>
            </div>
            <div className="cx-orow">
              <div className="grow"><div className="t">Affiliate</div><div className="d">{u.user_affiliate_id ? "Assigned to an affiliate" : "No affiliate"}</div></div>
              <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setShowAffiliateModal(true)}>{u.user_affiliate_id ? "Change" : "Assign"}</button></div>
            </div>
            <div className="cx-orow">
              <div className="grow"><div className="t">Extend validity</div><div className="d">Extend the plan without charging</div></div>
              <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => setStaticModal("extend")}>Extend</button></div>
            </div>
          </div>
        </div>
        <div className="cx-card">
          <div className="cx-card-h"><h6>Reset password</h6></div>
          <div className="cx-card-b">
            <div className="cx-cd-pw">
              <div><label className="cx-label">New password</label><input className="cx-input" type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} /></div>
              <div><label className="cx-label">Confirm password</label><input className="cx-input" type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} /></div>
              <button type="button" className="cx-btn cx-btn-primary cx-btn-sm" onClick={handlePasswordUpdate}>Update</button>
            </div>
            <p className="cx-help">At least 6 characters. The customer will need the new password to sign in.</p>
          </div>
        </div>
      </div>
      <div className="cx-card" style={{ borderColor: "#f3c0b8", alignSelf: "start" }}>
        <div className="cx-card-h" style={{ borderColor: "#f8e0da" }}><h6 style={{ color: "var(--coral)" }}>Danger zone</h6></div>
        <div className="cx-card-b">
          {u.user_status === 2 ? (
            <>
              <div className="cx-orow">
                <div className="grow"><div className="t">Restore workspace</div><div className="d">Move the customer out of the trash.</div></div>
                <div className="right"><button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => confirmStatusAction("5")}>Restore</button></div>
              </div>
              <div className="cx-orow">
                <div className="grow"><div className="t">Delete permanently</div><div className="d">This cannot be undone.</div></div>
                <div className="right"><button type="button" className="cx-btn cx-btn-danger cx-btn-sm" onClick={() => confirmStatusAction("6")}>Delete</button></div>
              </div>
            </>
          ) : (
            <>
              <div className="cx-orow">
                <div className="grow"><div className="t">{u.user_status === 1 ? "Deactivate workspace" : "Activate workspace"}</div><div className="d">{u.user_status === 1 ? "Broadcasts, bots and API calls stop right away." : "The workspace goes live again."}</div></div>
                <div className="right">
                  {u.user_status === 1
                    ? <button type="button" className="cx-btn cx-btn-danger cx-btn-sm" onClick={() => confirmStatusAction("2")}>Deactivate</button>
                    : <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => confirmStatusAction("1")}>Activate</button>}
                </div>
              </div>
              <div className="cx-orow">
                <div className="grow"><div className="t">Move to trash</div><div className="d">It can be restored later from the Trash tab.</div></div>
                <div className="right"><button type="button" className="cx-btn cx-btn-danger cx-btn-sm" onClick={() => confirmStatusAction("3")}>Move to trash</button></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderTab = () => {
    switch (activeTab) {
      case "billing": return renderBilling();
      case "wallet": return renderWallet();
      case "invoices": return renderInvoices();
      case "channels": return renderChannels();
      case "team": return <div className="cx-card"><div className="cx-card-h"><h6>Agents & roles</h6></div>{notConnected("ri-team-line", "Team members aren't available in the admin API yet")}</div>;
      case "automation": return renderAutomation();
      case "tickets": return <div className="cx-card"><div className="cx-card-h"><h6>Support tickets</h6><button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => navigate("/tickets")}><i className="ri-external-link-line"></i>Open inbox</button></div>{notConnected("ri-customer-service-2-line", "Tickets aren't linked to customers in the admin API yet")}</div>;
      case "activity": return renderActivity();
      case "settings": return renderSettings();
      default: return renderOverview();
    }
  };

  return (
    <div className="container-fluid">
      {isLoader && <Loader />}
      <div className="cx">
        <ul className="cx-cd-bc">
          <li><button type="button" onClick={() => navigate("/customers-and-users")}>Customers</button></li>
          <li>{name}</li>
        </ul>

        {/* --- Header card --- */}
        <div className="cx-card cx-cd-top">
          <div className="cx-cd-row">
            <div className="cx-cd-av">{initials}</div>
            <div className="cx-cd-info">
              <div className="cx-cd-name">
                {name}
                {u.user_status === 0 && <i className="ri-forbid-line" style={{ color: "var(--coral)", fontSize: 17 }} title="Inactive"></i>}
              </div>
              <div className="cx-cd-meta">
                {u.user_unique_id && <><span className="mono">#{u.user_unique_id}</span><span className="sep"></span></>}
                {owner && <><span>{owner}</span><span className="sep"></span></>}
                {u.user_email && <><a href={`mailto:${u.user_email}`}>{u.user_email}</a><span className="sep"></span></>}
                <span>Joined {fmtDate(u.createdAt)}</span>
              </div>
              <div className="cx-cd-pills">
                <Pill tone={sc}>{sl}</Pill>
                {u.user_plan_name && <Pill tone="blue">{u.user_plan_name}{u.user_plan_type ? ` · ${u.user_plan_type}` : ""}</Pill>}
                <Pill tone={credit ? "ok" : "none"}>{credit ? "Credit line" : "Prepaid"}</Pill>
                {obDone < 5 ? <Pill tone="warn">Onboarding {obDone}/5</Pill> : <Pill tone="ok">Onboarded</Pill>}
              </div>
            </div>
            <div className="cx-cd-actions">
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={handleDirectLogin}><i className="ri-login-circle-line"></i>Login as client</button>
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={openWallet}><i className="ri-coins-line"></i>Adjust wallet</button>
              <button type="button" className="cx-btn cx-btn-primary cx-btn-sm" onClick={() => setShowPlanModal(true)}><i className="ri-exchange-line"></i>Change plan</button>
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm cx-cd-more" title="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}><i className="ri-more-2-fill"></i></button>
            </div>
          </div>

          <div className="cx-cd-stats">
            <div><span className="l">Plan</span><b>{u.user_plan_name || "—"}</b><span className="d">{u.user_plan_type ? human(u.user_plan_type) : "no plan"}</span></div>
            <div><span className="l">Wallet balance</span><b className={Number(u.user_wallet_balance || 0) < 500 ? "down" : ""}>{inr(u.user_wallet_balance)}</b><span className="d">{credit ? "credit line enabled" : "no credit line"}</span></div>
            <div><span className="l">WhatsApp number</span><b className="mono" style={{ fontSize: 15 }}>{wa.display_phone_number || "—"}</b><span className="d">{wabaConnected ? `quality ${qLabel.toLowerCase()}` : "not connected"}</span></div>
            <div><span className="l">Messaging limit</span><b>{msgLimit ? human(msgLimit) : "—"}</b><span className="d">business-initiated / 24h</span></div>
            <div><span className="l">Next renewal</span><b className={planExpired ? "down" : ""}>{fmtDate(u.user_plan_end_date)}</b><span className="d">{planExpired ? "expired" : daysLeft !== null ? `${daysLeft} days left` : "—"}</span></div>
          </div>

          <div className="cx-cd-tabs">
            {CD_TABS.map(([k, ic, l]) => (
              <button type="button" key={k} className={activeTab === k ? "on" : ""} onClick={() => setActiveTab(k)}><i className={ic}></i>{l}</button>
            ))}
          </div>
        </div>

        {planExpired && (
          <div className="cx-banner">
            <i className="ri-error-warning-line"></i>
            <div><b>Plan expired</b> on {fmtDate(u.user_plan_end_date)}. <button type="button" className="cx-link" onClick={() => setShowPlanModal(true)}>Assign a plan →</button></div>
          </div>
        )}
        {u.user_status === 0 && (
          <div className="cx-banner" style={{ background: "#fdeeee", borderColor: "#f3c0b8" }}>
            <i className="ri-forbid-line" style={{ color: "var(--coral)" }}></i>
            <div><b>Workspace inactive</b> · all sends are stopped. <button type="button" className="cx-link" onClick={() => confirmStatusAction("1")}>Activate →</button></div>
          </div>
        )}

        {renderTab()}

        <RowMenu anchor={menuAnchor} title={name} items={menuItems} onClose={closeMenu} />
      </div>

      {/* Modals */}
      <AssignPlanModal show={showPlanModal} onHide={() => setShowPlanModal(false)} user={userData} onSuccess={onBillingChange} />
      <AssignAffiliateModal show={showAffiliateModal} onHide={() => { setShowAffiliateModal(false); getUserDetails(); }} userData={userData} />
      <WabaDetailsModal show={showWabaModal} onHide={() => setShowWabaModal(false)} userID={id || ""} initialData={userData.user_meta_account_info || {}} userData={userData} onSyncComplete={getUserDetails} />
      <ManageCreditsModal show={showCreditModal} onHide={() => setShowCreditModal(false)} user={userData} onSuccess={onBillingChange} />
      <EditProfileModal show={showProfileModal} onHide={() => { setShowProfileModal(false); setForm(mapResponseData(userData)); }} form={form} handleInputChange={handleInputChange} onSave={handleProfileUpdate} isSaving={isSaving} />
      <EditBusinessModal show={showBusinessModal} onHide={() => { setShowBusinessModal(false); setForm(mapResponseData(userData)); }} form={form} handleInputChange={handleInputChange} onSave={handleProfileUpdate} isSaving={isSaving} />
      <ConfirmActionModal action={confirmAction} onHide={() => setConfirmAction(null)} />

      {/* Static design modals */}
      <AdjustWalletModal show={staticModal === "wallet"} onHide={() => setStaticModal(null)} user={userData} />
      <ExtendValidityModal show={staticModal === "extend"} onHide={() => setStaticModal(null)} user={userData} />
    </div>
  );
};

export default CustomerDetails;
